import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { ArrowRight, ArrowUpRight, ChevronLeft, ChevronRight, Sparkles } from 'lucide-react'
import type { Vehicle } from '../../data/vehicles'
import type { BrandGroup } from './brandGroups'
import { pkr, shortPrice } from '../../utils/format'
import { prefersReducedMotion } from '../../animations/gsapAnimations'
import VehicleMedia from '../vehicle/VehicleMedia'

/* The Cars panel, whole: the floor one company at a time, as a row of cards.

   Each slide is a marque — Honda, Suzuki, Toyota — and the cars that stand
   for it, featured stock first. The track slides on a timer, holds while the
   pointer is on it, and every card is the car's own page one click away. */

const INTERVAL = 4600

const money = (vehicle: Vehicle) =>
  vehicle.instalment
    ? { value: pkr.format(vehicle.instalment), unit: '/mo' }
    : vehicle.price > 0
      ? { value: shortPrice(vehicle.price), unit: 'cash' }
      : { value: 'On request', unit: '' }

function FeatureCard({
  vehicle,
  shown,
  position,
  onNavigate,
}: {
  vehicle: Vehicle
  shown: boolean
  position: number
  onNavigate: () => void
}) {
  const price = money(vehicle)

  return (
    <Link
      to={`/cars/${vehicle.id}`}
      onClick={onNavigate}
      tabIndex={shown ? 0 : -1}
      style={{ transitionDelay: shown ? `${140 + position * 80}ms` : '0ms' }}
      className={`group/card relative flex flex-col overflow-hidden rounded-2xl border border-line bg-raised transition duration-600 ease-lux hover:-translate-y-1 hover:border-lime/50 hover:shadow-lift ${
        shown ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'
      }`}
    >
      {/* ---------- the picture ---------- */}
      <span className="group relative block aspect-16/10 overflow-hidden">
        <VehicleMedia vehicle={vehicle} source={vehicle.images[0] || ''} />
        <span
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 bg-linear-to-t from-raised via-transparent to-transparent"
        />
        <span className="absolute top-3 left-3 flex items-center gap-1.5 rounded-full bg-void/70 px-2.5 py-1 label-sm text-platinum ring-1 ring-white/10 backdrop-blur-md">
          {vehicle.brand}
          <span aria-hidden="true" className="size-1 rounded-full bg-lime" />
          <span className="text-steel tabular-nums">{vehicle.year}</span>
        </span>
        {vehicle.featured || vehicle.tag ? (
          <span className="absolute top-3 right-3 flex items-center gap-1 rounded-full bg-lime px-2.5 py-1 label-sm text-void shadow-action">
            <Sparkles aria-hidden="true" strokeWidth={2.4} className="size-3" />
            {vehicle.tag ?? 'Featured'}
          </span>
        ) : null}
      </span>

      {/* ---------- the words and the money ---------- */}
      <span className="flex flex-1 flex-col px-4 pt-3 pb-4">
        <strong className="truncate font-display text-[17px] font-bold tracking-[-0.02em] text-platinum">
          {vehicle.model}
        </strong>
        <span className="mt-0.5 truncate text-[12.5px] text-steel">
          {vehicle.variant || 'Standard'}
          {vehicle.colour ? ` · ${vehicle.colour}` : ''}
        </span>

        <span className="mt-4 flex items-end justify-between gap-3 border-t border-line-soft pt-3">
          <span className="min-w-0">
            <span className="block label-sm text-dim">
              {price.unit === '/mo' ? 'Instalment from' : 'Price'}
            </span>
            <span className="mt-1 flex items-baseline gap-1 font-display text-[18px] leading-none font-bold tracking-[-0.02em] text-lime tabular-nums">
              {price.value}
              {price.unit ? (
                <span className="font-sans text-[11px] font-medium tracking-normal text-dim">
                  {price.unit}
                </span>
              ) : null}
            </span>
          </span>
          <span
            aria-hidden="true"
            className="grid size-9 shrink-0 place-items-center rounded-full border border-lime/50 text-lime transition duration-300 group-hover/card:bg-lime group-hover/card:text-void"
          >
            <ArrowUpRight strokeWidth={2.2} className="size-4" />
          </span>
        </span>
      </span>
    </Link>
  )
}

type BrandSliderProps = {
  groups: BrandGroup[]
  loading: boolean
  total: number
  onNavigate: () => void
}

function BrandSlider({ groups, loading, total, onNavigate }: BrandSliderProps) {
  const [active, setActive] = useState(0)
  const [hovered, setHovered] = useState(false)
  const count = groups.length
  const slide = Math.min(active, Math.max(count - 1, 0))

  /* The timer restarts on every change of slide, so a manual pick gets a
     full interval before the track moves on. Held still under the pointer,
     and for anyone who asked the OS for less motion. */
  useEffect(() => {
    if (count < 2 || hovered || prefersReducedMotion()) return
    const id = window.setInterval(() => setActive((slide + 1) % count), INTERVAL)
    return () => window.clearInterval(id)
  }, [slide, count, hovered])

  const step = (delta: number) => setActive((slide + delta + count) % count)
  const current = groups[slide]

  return (
    <div
      className="flex flex-col p-5 lg:p-6"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onFocus={() => setHovered(true)}
      onBlur={() => setHovered(false)}
    >
      {/* ---------- head: the companies, and the way out ---------- */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <p className="label-sm shrink-0 text-dim">Featured by company</p>
          <div
            role="tablist"
            aria-label="Company"
            className="flex gap-1.5 overflow-x-auto rounded-full border border-line bg-raised p-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          >
            {loading
              ? Array.from({ length: 3 }, (_, index) => (
                  <span key={index} className="h-8 w-20 animate-pulse rounded-full bg-line" />
                ))
              : groups.map((group, index) => {
                  const selected = index === slide
                  return (
                    <button
                      key={group.brand}
                      type="button"
                      role="tab"
                      aria-selected={selected}
                      onClick={() => setActive(index)}
                      className={`flex shrink-0 items-center gap-2 rounded-full px-4 py-1.5 text-[13px] font-semibold transition duration-300 ${
                        selected
                          ? 'bg-lime text-void shadow-action'
                          : 'text-steel hover:bg-lime/10 hover:text-platinum'
                      }`}
                    >
                      {group.brand}
                      <span
                        className={`text-[11px] tabular-nums ${
                          selected ? 'text-void/70' : 'text-dim'
                        }`}
                      >
                        {group.count}
                      </span>
                    </button>
                  )
                })}
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Link
            to="/home#cars"
            onClick={onNavigate}
            className="mr-2 inline-flex items-center gap-1.5 text-[13px] font-medium text-lime transition-colors hover:text-lime-lift"
          >
            All {loading ? '' : `${total} `}cars
            <ArrowRight aria-hidden="true" strokeWidth={2} className="size-3.5" />
          </Link>
          <button
            type="button"
            onClick={() => step(-1)}
            disabled={count < 2}
            aria-label="Previous company"
            className="grid size-9 place-items-center rounded-full border border-line text-steel transition hover:border-lime hover:text-lime disabled:opacity-40"
          >
            <ChevronLeft aria-hidden="true" strokeWidth={2} className="size-4" />
          </button>
          <button
            type="button"
            onClick={() => step(1)}
            disabled={count < 2}
            aria-label="Next company"
            className="grid size-9 place-items-center rounded-full border border-line text-steel transition hover:border-lime hover:text-lime disabled:opacity-40"
          >
            <ChevronRight aria-hidden="true" strokeWidth={2} className="size-4" />
          </button>
        </div>
      </div>

      {/* ---------- the track ---------- */}
      <div className="relative mt-5 min-w-0 overflow-hidden">
        {loading ? (
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            {Array.from({ length: 4 }, (_, index) => (
              <span
                key={index}
                className="block h-[280px] animate-pulse rounded-2xl border border-line-soft bg-raised"
              />
            ))}
          </div>
        ) : count === 0 ? (
          <div className="flex items-center justify-center rounded-2xl border border-dashed border-line px-6 py-16 text-center text-[13px] text-dim">
            Fresh stock lands every week.
          </div>
        ) : (
          <div
            className="flex transition-transform duration-700 ease-lux"
            style={{ transform: `translateX(-${slide * 100}%)` }}
          >
            {groups.map((group, index) => {
              const shown = index === slide
              return (
                <div
                  key={group.brand}
                  role="tabpanel"
                  aria-hidden={!shown}
                  className="grid w-full min-w-0 shrink-0 basis-full gap-4 md:grid-cols-2 xl:grid-cols-4"
                >
                  {group.cars.map((vehicle, position) => (
                    <FeatureCard
                      key={vehicle.id}
                      vehicle={vehicle}
                      shown={shown}
                      position={position}
                      onNavigate={onNavigate}
                    />
                  ))}
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* ---------- foot: where we are, and the rest of this marque ---------- */}
      {count > 0 ? (
        <div className="mt-5 flex items-center justify-between gap-4">
          <div className="flex items-center gap-1.5" aria-hidden="true">
            {groups.map((group, index) => (
              <button
                key={group.brand}
                type="button"
                tabIndex={-1}
                onClick={() => setActive(index)}
                className={`h-1.5 rounded-full transition-all duration-500 ${
                  index === slide ? 'w-8 bg-lime' : 'w-2 bg-line hover:bg-dim'
                }`}
              />
            ))}
          </div>
          {current ? (
            <Link
              to={`/home?brand=${encodeURIComponent(current.brand)}#cars`}
              onClick={onNavigate}
              className="inline-flex items-center gap-1.5 text-[13px] font-medium text-steel transition-colors hover:text-lime"
            >
              Every {current.brand} on the floor
              <span className="text-dim tabular-nums">({current.count})</span>
              <ArrowRight aria-hidden="true" strokeWidth={2} className="size-3.5" />
            </Link>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}

export default BrandSlider
